import { CesrNumber, RotateIdentifierArgs, SignifyClient } from 'signify-ts'

import { isEstEvent, waitOperationWithRetries } from './util'
import { GroupAid, Identifier } from '@/state/signify'
import {
  getFirstWitnessFromEnds,
  getLocalMemberFromGroupAid,
  isGroupAid
} from '@/utils/aid'
import { getGroupMembersSigningThreshold } from './multisig-aid'
import { formatUrlPath } from '@/utils/common'
import { KELEvent } from './types'
import { consoleLog } from '@/utils/console-logger'
import { retryIf, RetryOptions } from '@/utils/retry'

/**
 * Minimal shape of an identifier entry as returned by signify-ts
 * `identifiers().list()`. The upstream type wraps the array in
 * `{ aids: IdentifierSummary[] }` but does not declare this wrapper,
 * so we model the element shape locally and cast at the access site.
 */
type IdentifierSummary = { prefix: string; name?: string }

/**
 * List of managed identifiers
 * @async
 * @param {number} [start=0] Start index of list of identifiers, defaults to 0
 * @param {number} [end=50] End index of list of identifiers, defaults to 50
 * @returns {Promise<any>} A promise to the list of managed identifiers
 */
export const getIdentifiers = async (
  client: SignifyClient,
  start: number = 0,
  end: number = 50
): Promise<Identifier[]> => {
  let result = await client.identifiers().list(start, end)
  let aids = await parseIdentifierDtoList(client, result.aids)
  return aids
}

/**
 * Get identifier detail by alias
 * @async
 * @param {string} name Name or alias of the identifier
 * @returns {Promise<any>} A promise to the identifier information
 */
export const getIdentifier = async (
  client: SignifyClient,
  alias: string,
  requiresRawData: boolean = false
): Promise<Identifier> => {
  let identifier = await client.identifiers().get(alias)
  const parsedIdentifier = await parseIdentifierDto(
    client,
    identifier,
    requiresRawData
  )
  return parsedIdentifier
}

/**
 * Retrieve key events for an identifier
 * @async
 * @param {string} aid Identifier/AID prefix
 * @returns {Promise<any>} A promise to the key events
 */
export const getKeyEvents = async (
  client: SignifyClient,
  aid: string
): Promise<any> => {
  return await client.keyEvents().get(aid)
}

/**
 * Retrieve the key state for an identifier
 * @async
 * @param {string} aid Identifier prefix
 * @returns {Promise<any>} A promise to the key states
 */
export const getKeyState = async (
  client: SignifyClient,
  aid: string
): Promise<any> => {
  return await client.keyStates().get(aid)
}

/**
 * Query the key state of an identifier for a given sequence number or anchor SAID
 * @async
 * @param {string} aid Identifier prefix
 * @param {string} [sn] Optional sequence number
 * @param {string} [anchor] Optional anchor SAID
 * @returns {Promise<any>} A promise to the long-running operation
 */
export const queryKeyState = async (
  client: SignifyClient,
  aid: string,
  sn?: string,
  anchor?: string
): Promise<any> => {
  return await client.keyStates().query(aid, sn, anchor)
}

/**
 * Retrieve the key states for a list of identifiers
 * @async
 * @param {Array<string>} aids List of identifier prefixes
 * @returns {Promise<any>} A promise to the key states
 */
export const getKeyStates = async (
  client: SignifyClient,
  aids: string[]
): Promise<any> => {
  return await client.keyStates().list(aids)
}

/**
 * Find last establishment event from the list of events
 * @async
 * @param {Array<any>} events List of events
 * @returns {Promise<any>} Last Est event or undefined
 */
export const findLastEstEvent = (events: any[]): any | undefined => {
  return events.reduce((lastEstEvent, currentEvent) => {
    if (!isEstEvent(currentEvent.t)) {
      return lastEstEvent
    }

    const currentSn = parseInt(currentEvent.s, 10)
    const lastSn = lastEstEvent ? parseInt(lastEstEvent.s, 10) : -Infinity

    return currentSn > lastSn ? currentEvent : lastEstEvent
  }, undefined)
}

export const findLocalAidFromSmids = async (
  client: SignifyClient,
  smids: string[]
) => {
  let aids = await client.identifiers().list(0, 24)
  // `aids['aids']` is the signify-ts response wrapper; see IdentifierSummary above.
  const local = (aids['aids'] as IdentifierSummary[]).filter((elem) => {
    return smids.some((x) => {
      return x === elem.prefix
    })
  })
  const localName = local?.[0]?.name
  if (!localName) {
    // No local AID matches any of the supplied smids — caller's contract
    // is to receive undefined in that case (consumers null-check).
    return undefined
  }
  let localAID = await client.identifiers().get(localName)

  return localAID
}

export const findIdentifierByAlias = (
  aids: Identifier[] | null | undefined,
  alias: string | null | undefined
): Identifier | undefined => {
  if (!aids || !alias) {
    return undefined
  }

  return aids.find((aid) => aid.name === alias)
}

export const findIdentifierByAid = (
  idenfitier: Identifier[] | null | undefined,
  aid: string | null | undefined
): Identifier | undefined => {
  if (!idenfitier || !aid) {
    return undefined
  }

  return idenfitier.find((i) => i.prefix === aid)
}

export const getExnMessageBySaid = async (
  client: SignifyClient,
  said: string
): Promise<any> => {
  let exnRes = await client.exchanges().get(said)
  return exnRes
}

export const parseIdentifierDto = async (
  client: SignifyClient,
  identifierDto: any,
  includeRawData: boolean = false
): Promise<Identifier> => {
  let isGroup = isGroupAid(identifierDto)
  let group: GroupAid | undefined = undefined
  if (isGroup === true) {
    let members = await getGroupMembersSigningThreshold(
      client,
      identifierDto.name
    )
    let localAid = getLocalMemberFromGroupAid(identifierDto)
    let witness = getFirstWitnessFromEnds(
      members?.find((x) => x.aid === localAid?.prefix)?.ends
    )
    group = {
      name: identifierDto.name,
      prefix: identifierDto.prefix,
      localAid: localAid,
      members: members ?? undefined,
      minSignersThreshold:
        members && members.length > 0 ? members[0].minSignersThreshold : 0,
      witnessOobi: witness?.http
        ? (formatUrlPath(witness.http, `oobi/${identifierDto.prefix}`) ??
          undefined)
        : undefined
    }
  }

  const identifier: Identifier = {
    name: identifierDto.name,
    prefix: identifierDto.prefix,
    isMultisig: isGroup,
    group: group
  }

  if (includeRawData === true) identifier.raw = identifierDto

  return identifier
}

export const parseIdentifierDtoList = async (
  client: SignifyClient,
  aids: any[]
): Promise<Identifier[]> => {
  let identifiers: Identifier[] = []
  if (aids && aids?.length > 0) {
    for (let i = 0; i < aids.length; i++) {
      const identifier = await parseIdentifierDto(client, aids[i])
      identifiers.push(identifier)
    }
  }
  return identifiers
}

export const addEndRole = async (
  client: SignifyClient,
  alias: string,
  prefix: string,
  role: string
) => {
  try {
    let res = await client.identifiers().addEndRole(alias, role, prefix)
    await waitOperationWithRetries(client, await res.op(), 10)
    //await delay(2000)
    const oobiRes = await client.oobis().get(alias, role)
    if (oobiRes && oobiRes?.oobis.length < 1) {
      await client.identifiers().addEndRole(alias, role, prefix)
    }
  } catch (ex) {
    console.error('Error in addEndRole', ex)
    throw ex
  }
}

/**
 * Get list of end role authorizations for a Keri idenfitier
 */
export const getEndRoles = async (
  client: SignifyClient,
  alias: string,
  role?: string
): Promise<any> => {
  const path =
    role !== undefined
      ? `/identifiers/${alias}/endroles/${role}`
      : `/identifiers/${alias}/endroles`
  const response: Response = await client.fetch(path, 'GET', null)
  if (!response.ok) throw new Error(await response.text())
  const result = await response.json()
  return result
}

/**
 * Test if end role is authorized for a Keri identifier
 */
export const hasEndRole = async (
  client: SignifyClient,
  alias: string,
  role: string,
  eid: string
): Promise<boolean> => {
  const list = await getEndRoles(client, alias, role)
  for (const i of list) {
    if (i.role === role && i.eid === eid) {
      return true
    }
  }
  return false
}

export const getRegistries = async (client: SignifyClient, name: string) => {
  return await client.registries().list(name)
}

/**
 * Rename a registry
 * @async
 * @param {string} alias Name or alias of the identifier
 * @param {string} registryName Current registry name
 * @param {string} newName New registry name
 * @returns {Promise<any>} A promise to the registry record
 */
export const renameRegistry = async (
  client: SignifyClient,
  alias: string,
  registryName: string,
  newName: string
) => {
  return await client.registries().rename(alias, registryName, newName)
}

/**
 * Rotate a single-sig identifier
 * @async
 * @param {string} name Name or alias of the identifier
 * @param {RotateIdentifierArgs} args Optional arguments related rotating an identfier
 * @returns {Promise<any>} A promise to the long running operation response
 */
export const rotateSingleSigIdentifier = async (
  client: SignifyClient,
  alias: string,
  args: RotateIdentifierArgs = {}
) => {
  let rotResult = await client.identifiers().rotate(alias, args)
  let op = await waitOperationWithRetries(client, await rotResult.op(), 10)
  return op
}

/**
 * Approve an event of delegatee using an interaction event
 * @async
 * @param {string} name Name or alias of the identifier
 * @param {object} [anchor] Event data to be anchored in the interaction event.
 * @returns {Promise<EventResult>} A promise to the interaction event result
 */
export const approveDelegation = async (
  client: SignifyClient,
  name: string,
  anchor: { i: string; s: string; d: string }
) => {
  return await client.identifiers().interact(name, anchor)
}

export const checkIfEventIsInKEL = async (
  client: SignifyClient,
  alias: string,
  event: KELEvent | null,
  checkIsWitnessed: boolean
): Promise<boolean | null> => {
  consoleLog(`Checking if KEL of identifier ${alias} has event: `, event)
  if (!event) return null

  let aid = await client.identifiers().get(alias)
  if (!aid) return null

  consoleLog(
    `Identifier ${alias} current state is at seq num ${aid.state?.s} and event said is: `,
    ` with digest ${aid.state?.d}`
  )
  consoleLog('Event seq num: ', event.s)
  consoleLog('Event digest: ', event.d)
  consoleLog(
    `Identifer ${alias} witness count: ${aid.state?.b?.length}, witness threshold: ${aid.state?.bt}`
  )
  consoleLog('Windexes: ', aid.windexes)

  const snerCurrent = new CesrNumber({}, aid.state?.s)
  const snerEvent = new CesrNumber({}, event.s)
  const windexLength = Number(aid.windexes?.length || 0)
  const backerThreshold = Number(aid.state?.bt)

  if (snerEvent.num === snerCurrent.num && event.d === aid.state?.d) {
    if (checkIsWitnessed === false) {
      console.debug(
        '[aid.ts] : num == num && digest == digest && no witnesscheck'
      )
      return true
    }

    // aid.windexes?.length can be (string[] | null)
    // This will only work for simple thresholds (ie integers) bc:
    // aid.state?.bt is (string) and thresholds in KERI can be:
    //  simple (integer ie "2" for 2 of n) or
    //  complex (string of thresholds or weighted thresholds) "[1,1,1/2]"
    if (windexLength >= backerThreshold) {
      console.debug(
        '[aid.ts] : num == num && digest == digest && witnesses checked'
      )
      return true
    } else {
      console.debug(
        '[aid.ts] : num == num && digest == digest && witnesses failed to check'
      )
      return false
    }
  } else if (snerEvent.num < snerCurrent.num) {
    console.debug('[aid.ts] : snerEvent.num < snerCurrent.num')
    // For now assume that event is in KEL.
    //TODO: For now we can get all events and then filter by seq num and then check if it is witnessed.
    return true
  } else {
    console.debug('[aid.ts] : catchall false')
    return false
  }
}

export async function waitForEventToBeInKEL(
  client: SignifyClient,
  alias: string,
  event: KELEvent,
  checkIsWitnessed: boolean,
  maxRetries: number = 60
): Promise<boolean | null> {
  const retryOptions: RetryOptions = {
    maxRetries: maxRetries,
    timeout: 600000 // 10 minutes
  }
  const condition = (data: boolean | null): boolean => {
    if (data === true) return true
    return false
  }

  let isAdded = await retryIf(
    () => checkIfEventIsInKEL(client, alias, event, checkIsWitnessed),
    condition,
    retryOptions
  )
  return isAdded
}
